import { For, Show, type Component } from 'solid-js';

import { isReturnGate } from '../net/protocol';
import type { GameStore } from '../state/store';
import { iconPath, type IconName } from './icons';
import { STATIONS } from './stations';

/**
 * Icons pinned over the things in the world worth walking to.
 *
 * A zone marked its entry and exit with two coloured discs painted on the floor,
 * which at the gameplay camera are two ellipses — indistinguishable from each
 * other and from a shadow. These are the same anchors the nameplates use, with an
 * icon and a label, so "the way out" reads as the way out.
 *
 * Follows the world-anchored HUD rule exactly: **depends on `zone()`, never on
 * `anchors()`**. `anchors` is written every frame, so a list derived from it
 * re-keys its `<For>` at display refresh rate; each marker reads its own anchor
 * inside itself instead, which makes a frame two style writes per marker.
 *
 * **Only the town waypoint is clickable, and that asymmetry is the design.** In town
 * the waypoint *is* the way out, so clicking it opens the level select — it had been
 * a label over a floor tile that did nothing at all, which reads as broken when it
 * is the most prominent thing in the sanctuary. Everything else here is a passive
 * label, because reaching it is done by **walking onto it**: the exit and an open
 * town portal are both *places*, checked server-side at the end of `Step`, and a
 * click target on either would be a second way to do one thing.
 *
 * A town portal is the one marker that is not part of the layout, so it depends on
 * `portals()` — refreshed per snapshot at 30 Hz — rather than on `anchors()`.
 */
export const WorldMarkers: Component<{ store: GameStore }> = (props) => {
  const marks = () => {
    const zone = props.store.zone();
    if (!zone) return [];

    const inHub = zone.kind === 'HUB';
    return [
      {
        id: 'zone-entry',
        // Town has no "entry" worth marking — you are standing in it, and the
        // thing you want in town is the portal.
        icon: 'entry' as IconName,
        testid: 'marker-entry',
        label: inHub ? '' : 'Arrival',
        at: zone.spawn,
        opens: false,
      },
      {
        id: 'zone-exit',
        icon: inHub ? ('portal' as IconName) : ('exit' as IconName),
        testid: inHub ? 'marker-portal' : 'marker-exit',
        label: inHub ? 'The Grind' : 'Onward',
        at: zone.exit,
        opens: inHub,
      },
      // The gate, whichever side of it is standing. At most one, usually none.
      // Labelled as somewhere to *go*, since that is the whole gesture — the button
      // that opened it did not move anybody.
      //
      // Its own test id rather than one derived from the icon, which it shares with
      // town's waypoint: two markers that look the same are two different things, and
      // one of them is clickable. The return gate gets a *third* id for the same
      // reason — in town it stands a tile from the waypoint, and "which of these two
      // glowing rings am I looking at" is the whole question a label answers.
      //
      // Neither side is clickable. Both are places the server checks at the end of
      // `Step`, so a click target would be a second way to do one thing.
      // The five workshops, named where they stand.
      //
      // A **label, not a button.** The artisan is the click target and they are in the
      // 3D scene, which is where a world-anchored click belongs: a `.hud-interactive`
      // box in the lower middle of an isometric view is exactly what swallowed the
      // clicks meant for the monsters standing there. So this says *which shop that is*
      // and nothing else — the same relationship the exit's label has to the exit.
      //
      // Off `stations()`, which is the ZONE frame, never off `anchors()`.
      ...props.store.stations().map((station) => ({
        id: `station-${station.kind}`,
        icon: (STATIONS.find((s) => s.kind === station.kind)?.icon ?? 'portal') as IconName,
        testid: `marker-station-${station.kind}`,
        label: STATIONS.find((s) => s.kind === station.kind)?.name ?? 'Workshop',
        at: station.at,
        opens: false,
      })),
      ...props.store.portals().map((portal) => ({
        id: portal.id,
        icon: 'portal' as IconName,
        testid: isReturnGate(portal) ? 'marker-return-gate' : 'marker-town-portal',
        // A return gate says what is behind it. The depth is the server's — in town
        // the zone frame describes the hub, so it is the one thing the client could
        // not work out — and without it the marker would be an unlabelled ring
        // beside another unlabelled ring.
        label: isReturnGate(portal)
          ? `Return · Depth ${portal.level ?? '?'}`
          : 'Town Portal',
        at: { x: portal.x, z: portal.z },
        opens: false,
      })),
    ].filter((m) => m.label !== '');
  };

  return (
    <div class="markers" data-testid="world-markers">
      <For each={marks()}>
        {(mark) => {
          const anchor = () => props.store.anchors()[mark.id];
          const path = iconPath(mark.icon);

          const body = (
            <>
              <svg class="marker__icon" viewBox={`0 0 ${path.width} ${path.height}`} aria-hidden="true">
                <path d={path.d} />
              </svg>
              <span class="marker__label">{mark.label}</span>
            </>
          );

          return (
            <Show when={anchor()?.visible}>
              <Show
                when={mark.opens}
                fallback={
                  <div
                    class="marker"
                    data-testid={mark.testid}
                    style={{ left: `${anchor()?.x ?? 0}px`, top: `${anchor()?.y ?? 0}px` }}
                  >
                    {body}
                  </div>
                }
              >
                {/* `.markers` is pointer-transparent like the rest of the HUD, so this
                    opts back in on itself rather than on the layer. */}
                <button
                  type="button"
                  class="marker marker--action hud-interactive"
                  data-testid={mark.testid}
                  aria-label="Open the Grind portal"
                  style={{ left: `${anchor()?.x ?? 0}px`, top: `${anchor()?.y ?? 0}px` }}
                  onClick={() => props.store.openMenu('portal')}
                >
                  {body}
                </button>
              </Show>
            </Show>
          );
        }}
      </For>
    </div>
  );
};
